/**
 * Axios client — futur ré-implémentable côté natif (Swift URLSession / Kotlin OkHttp).
 * Ne PAS coupler à React ici (logique transport pure).
 */
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  headers: {
    Accept: "application/json",
  },
  withCredentials: false,
});

// ---------------------------------------------------------------------------
// Auth token injection + 401 auto-refresh
// ---------------------------------------------------------------------------
// We avoid a static import of the store here to prevent a circular dependency.
// The provider that owns the store is set at app boot via `setAuthProvider`.

type AuthProvider = {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  refreshAccess: () => Promise<string | null>;
  signOut: () => Promise<void>;
};

let authProvider: AuthProvider | null = null;

export function setAuthProvider(p: AuthProvider): void {
  authProvider = p;
}

const NO_AUTH_PATHS = [
  "/api/v1/auth/login",
  "/api/v1/auth/refresh",
  "/api/v1/auth/setup-password",
  "/api/v1/auth/apply",
  "/api/v1/auth/sso/providers",
  "/api/v1/auth/sso/callback",
  "/healthz",
  "/api/v1/info",
];

// The SSO start path carries the provider id, so it cannot be matched literally.
// `/me/sso/...` is deliberately NOT here: linking requires a session.
const NO_AUTH_PATTERNS = [/\/api\/v1\/auth\/sso\/[^/]+\/start$/];

function shouldSkipAuth(url: string | undefined): boolean {
  if (!url) return false;
  return (
    NO_AUTH_PATHS.some((p) => url.endsWith(p)) || NO_AUTH_PATTERNS.some((re) => re.test(url))
  );
}

apiClient.interceptors.request.use((config) => {
  if (!authProvider) return config;
  if (shouldSkipAuth(config.url)) return config;
  const token = authProvider.getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!authProvider) return Promise.reject(error);
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retried && !shouldSkipAuth(original.url)) {
      original._retried = true;
      const newToken = await authProvider.refreshAccess();
      if (newToken) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient.request(original);
      }
      await authProvider.signOut();
    }
    return Promise.reject(error);
  },
);
